import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtOdds, apiError } from "../lib/api";
import { Trash2, RefreshCw, Eraser, Send, Ticket, Sparkles, Check, X, Percent, Eye, EyeOff, Wand2 } from "lucide-react";

const EMPTY = { category: "lock", player_name: "", team: "", bet: "To Hit a HR", odds: 200, confidence: 7.0, note: "", mlb_id: null, pick_type: "batter", sport: "mlb" };

const SPORT_OPTIONS = ["mlb", "nfl", "nba", "nhl", "ncaaf", "ncaab"];

const TYPE_HINTS = {
  batter: { player: "Aaron Judge", bet: "To Hit a HR / 2+ Hits / 2+ Total Bases" },
  pitcher: { player: "Paul Skenes", bet: "Over 6.5 Strikeouts / Under 1.5 Walks" },
  fish: { player: "NYY @ BOS", bet: "Yankees ML -145 / Over 8.5 / BOS +1.5" },
};

const normName = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\./g, "");

const AFFILIATE_BOOKS = ["DraftKings", "FanDuel", "BetMGM", "Caesars", "Hard Rock Bet", "BetRivers", "Fliff", "Bally Bet", "betPARX", "bet365", "William Hill", "Pinnacle", "bwin", "Unibet"];

function FreeModeSection() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["siteStatus"], queryFn: () => api.get("/site-status").then((r) => r.data) });
  const on = !!data?.free_mode;
  const toggleMut = useMutation({
    mutationFn: () => api.post("/admin/free-mode", { enabled: !on }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["siteStatus"] });
      toast.success(r.data.free_mode ? "🎉 FREE DAY IS LIVE — the whole site is unlocked for everyone!" : "Free day is OFF — paywalls are back on.");
    },
    onError: (e) => toast.error(apiError(e)),
  });
  return (
    <div className={`rounded-xl p-4 fade-up-2 border ${on ? "bg-[#0a2413] border-[#22c55e]/60 glow-pulse" : "card-surface"}`} data-testid="admin-free-mode-section">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="font-display text-lg font-bold uppercase flex items-center gap-2">
            Free-For-All Mode
            <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ${on ? "bg-[#22c55e] text-black" : "bg-white/10 text-neutral-400"}`} data-testid="free-mode-status">
              {on ? "LIVE" : "OFF"}
            </span>
          </div>
          <p className="text-xs text-neutral-500 mt-1">Flip this on during your videos — every Pro/VIP tab unlocks for all visitors instantly, and a "FREE DAY" banner shows site-wide. Flip it off whenever you want.</p>
        </div>
        <button onClick={() => toggleMut.mutate()} disabled={toggleMut.isPending} data-testid="free-mode-toggle-btn"
          className={`font-black uppercase tracking-wider text-sm px-6 py-3 rounded-lg transition-colors duration-200 disabled:opacity-50 ${on ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[#22c55e] hover:bg-[#1ea550] text-black"}`}>
          {toggleMut.isPending ? "..." : on ? "End Free Day" : "Make It Free"}
        </button>
      </div>
    </div>
  );
}

function AffiliateLinksSection() {
  const qc = useQueryClient();
  const [links, setLinks] = useState(null);
  const { data } = useQuery({ queryKey: ["adminBookLinks"], queryFn: () => api.get("/book-links").then((r) => r.data) });
  const { data: clicks } = useQuery({ queryKey: ["adminBookClicks"], queryFn: () => api.get("/admin/book-clicks").then((r) => r.data) });
  const clickMap = Object.fromEntries((clicks?.books || []).map((b) => [b.book, b]));
  const topBook = (clicks?.books || [])[0];
  const affiliate = new Set(data?.affiliate || []);
  const current = links || Object.fromEntries(AFFILIATE_BOOKS.map((b) => [b, affiliate.has(b) ? (data?.links?.[b] || "") : ""]));
  const saveMut = useMutation({
    mutationFn: () => api.post("/admin/affiliate-links", { links: current }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["adminBookLinks"] });
      qc.invalidateQueries({ queryKey: ["bookLinks"] });
      setLinks(null);
      toast.success(`Affiliate links saved — ${r.data.active} active`);
    },
    onError: (e) => toast.error(apiError(e)),
  });
  return (
    <div className="card-surface rounded-xl p-4 fade-up-3" data-testid="admin-affiliate-section">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <div className="font-display text-lg font-bold uppercase">Affiliate Links</div>
        <span className="text-[10px] uppercase tracking-widest font-bold bg-[#22c55e]/15 text-[#22c55e] border border-[#22c55e]/40 px-2 py-0.5 rounded-full" data-testid="affiliate-active-count">
          {affiliate.size} active
        </span>
        <span className="text-[10px] uppercase tracking-widest font-bold bg-[#a855f7]/15 text-[#a855f7] border border-[#a855f7]/40 px-2 py-0.5 rounded-full" data-testid="affiliate-total-clicks">
          {clicks?.total_clicks ?? 0} tap-throughs
        </span>
        {topBook && topBook.total > 0 && (
          <span className="text-[10px] text-neutral-500">Top earner: <b className="text-neutral-300">{topBook.book}</b> ({topBook.total} clicks, {topBook.last7} this week)</span>
        )}
      </div>
      <p className="text-xs text-neutral-500 mb-3">Paste your referral/affiliate URL for each sportsbook. Every book button and best-price tap-through across the site will use it. Leave blank to use the book's homepage.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {AFFILIATE_BOOKS.map((b) => (
          <div key={b} className="flex items-center gap-2">
            <span className={`w-28 shrink-0 text-[11px] font-bold uppercase tracking-wide ${affiliate.has(b) ? "text-[#22c55e]" : "text-neutral-400"}`}>
              {b}
              {clickMap[b]?.total > 0 && <span className="block text-[9px] font-data text-[#a855f7] normal-case" data-testid={`affiliate-clicks-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{clickMap[b].total} clicks · {clickMap[b].last7} /7d</span>}
            </span>
            <input value={current[b] || ""} onChange={(e) => setLinks({ ...current, [b]: e.target.value })}
              data-testid={`affiliate-input-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              placeholder="https://your-referral-link..."
              className="flex-1 min-w-0 bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-2 py-1.5 text-xs font-data outline-none transition-colors duration-200" />
          </div>
        ))}
      </div>
      <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending || !links} data-testid="affiliate-save-btn"
        className="mt-3 bg-[#a855f7] hover:bg-[#9333ea] disabled:opacity-40 text-black font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded transition-colors duration-200">
        {saveMut.isPending ? "Saving..." : "Save Affiliate Links"}
      </button>
    </div>
  );
}

function SuggOdds({ s, qc, date }) {
  const [v, setV] = useState(String(s.odds));
  const mut = useMutation({
    mutationFn: () => api.patch(`/admin/suggestions/${s.id}`, { odds: parseInt(v, 10) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminSuggestions", date] }); toast.success("Odds updated"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const dirty = parseInt(v, 10) !== s.odds && !isNaN(parseInt(v, 10));
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      <input value={v} onChange={(e) => setV(e.target.value)} data-testid={`sugg-odds-input-${s.id}`}
        title="Edit the odds before approving — the board pick uses this number"
        className="w-16 bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-1.5 py-0.5 font-data text-xs text-[#22c55e] outline-none transition-colors duration-200" />
      {dirty && (
        <button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid={`sugg-odds-save-${s.id}`} title="Save odds"
          className="p-1 rounded border border-[#22c55e]/50 text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors duration-200">
          <Check size={11} />
        </button>
      )}
    </span>
  );
}

export default function Admin() {
  const qc = useQueryClient();
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date());
  const [date, setDate] = useState(today);
  const [form, setForm] = useState(EMPTY);
  const [rosterQ, setRosterQ] = useState("");
  const [showEngine, setShowEngine] = useState(false);

  const { data: picksData } = useQuery({ queryKey: ["adminPicks", date], queryFn: () => api.get(`/admin/picks?date=${date}`).then((r) => r.data) });
  const { data: sportSlate } = useQuery({
    queryKey: ["adminSlate", form.sport],
    queryFn: () => api.get(`/${form.sport}/board`).then((r) => r.data),
    enabled: form.sport === "nfl" || form.sport === "ncaaf",
  });
  const sportGames = (sportSlate?.days || []).flatMap((d) => d.games.map((g) => ({ label: `${g.away.name} @ ${g.home.name}`, time: g.time_et })));
  const { data: suggData } = useQuery({ queryKey: ["adminSuggestions", date], queryFn: () => api.get(`/admin/suggestions?date=${date}`).then((r) => r.data) });
  const { data: usersData } = useQuery({ queryKey: ["adminUsers"], queryFn: () => api.get("/admin/users").then((r) => r.data) });
  const { data: playersData } = useQuery({ queryKey: ["players"], queryFn: () => api.get("/players").then((r) => r.data) });
  const { data: allPitchers } = useQuery({ queryKey: ["allPitchers"], queryFn: () => api.get("/pitchers/all").then((r) => r.data) });
  const { data: promo } = useQuery({ queryKey: ["promo"], queryFn: () => api.get("/promo").then((r) => r.data) });
  const { data: subPromo } = useQuery({ queryKey: ["subPromo"], queryFn: () => api.get("/sub-promo").then((r) => r.data) });
  const { data: revenue } = useQuery({ queryKey: ["adminRevenue"], queryFn: () => api.get("/admin/revenue").then((r) => r.data) });
  const { data: linesData } = useQuery({ queryKey: ["gameLines"], queryFn: () => api.get("/game-lines").then((r) => r.data) });

  const rosterMatches = (() => {
    const q = normName(rosterQ.trim());
    if (q.length < 2) return [];
    if (form.pick_type === "pitcher") {
      const pitchers = allPitchers?.pitchers || [];
      return pitchers
        .filter((p) => normName(p.name).includes(q))
        .slice(0, 8)
        .map((p) => ({ mlb_id: p.id, name: p.name, team: p.team, position: "P", headshot: `https://img.mlbstatic.com/mlb-photos/image/upload/w_120,q_100/v1/people/${p.id}/headshot/67/current` }));
    }
    const players = playersData?.players || [];
    const starts = [];
    const contains = [];
    for (const p of players) {
      const n = normName(p.name);
      if (n.startsWith(q) || n.split(" ").some((w) => w.startsWith(q))) starts.push(p);
      else if (n.includes(q)) contains.push(p);
    }
    return [...starts, ...contains].slice(0, 8);
  })();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["adminPicks", date] });
    qc.invalidateQueries({ queryKey: ["adminSuggestions", date] });
    qc.invalidateQueries({ queryKey: ["board"] });
    qc.invalidateQueries({ queryKey: ["tracker"] });
  };

  const createMut = useMutation({
    mutationFn: () => api.post("/admin/picks", { ...form, date, odds: Number(form.odds), confidence: Number(form.confidence) }),
    onSuccess: () => { invalidate(); setForm(EMPTY); toast.success("Pick posted"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const delMut = useMutation({ mutationFn: (id) => api.delete(`/admin/picks/${id}`), onSuccess: () => { invalidate(); toast.success("Deleted"); } });
  const gradeMut = useMutation({
    mutationFn: ({ id, result }) => api.post(`/admin/picks/${id}/grade`, { result }),
    onSuccess: () => { invalidate(); toast.success("Graded"); },
  });
  const tierMut = useMutation({
    mutationFn: ({ id, tier }) => api.patch(`/admin/users/${id}/tier`, { tier }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminUsers"] }); toast.success("Tier updated"); },
  });
  const resetLinkMut = useMutation({
    mutationFn: (id) => api.post(`/admin/users/${id}/reset-link`),
    onSuccess: async (r) => {
      const link = r.data.reset_link;
      try { await navigator.clipboard.writeText(link); toast.success(`Reset link for ${r.data.email} copied to clipboard${r.data.emailed ? " and emailed to the member" : ""} (expires in 1h)`); }
      catch (e) { window.prompt("Copy this reset link (expires in 1h):", link); }
    },
    onError: (e) => toast.error(apiError(e)),
  });
  const refreshMut = useMutation({
    mutationFn: () => api.post("/admin/refresh-stats"),
    onSuccess: () => { qc.invalidateQueries(); toast.success("MLB stats refreshed"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const resetHistoryMut = useMutation({
    mutationFn: () => api.post("/admin/reset-history"),
    onSuccess: (r) => { qc.invalidateQueries(); toast.success(`History cleared — ${r.data.deleted} old picks deleted. Stats now start from 0.`); },
    onError: (e) => toast.error(apiError(e)),
  });
  const discordMut = useMutation({
    mutationFn: () => api.post("/admin/discord/post-board"),
    onSuccess: (r) => toast.success(`Board posted to Discord (${r.data.picks} picks)`),
    onError: (e) => toast.error(apiError(e)),
  });
  const promoMut = useMutation({
    mutationFn: (active) => api.post("/admin/promo", { active }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["promo"] }); toast.success(r.data.active ? "$1 Day Pass launch promo is ON" : "Launch promo turned OFF — Day Pass back to $9.99"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const suggApproveMut = useMutation({
    mutationFn: (sid) => api.post(`/admin/suggestions/${sid}/approve`),
    onSuccess: () => { invalidate(); toast.success("Pick approved and posted to the board"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const suggApproveAllMut = useMutation({
    mutationFn: () => api.post(`/admin/suggestions/approve-all?date=${date}`),
    onSuccess: (r) => { invalidate(); toast.success(`${r.data.approved} picks approved and posted to the board`); },
    onError: (e) => toast.error(apiError(e)),
  });
  const suggDismissMut = useMutation({
    mutationFn: (sid) => api.post(`/admin/suggestions/${sid}/dismiss`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminSuggestions", date] }); toast.success("Suggestion dismissed"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const suggRegenMut = useMutation({
    mutationFn: () => api.post(`/admin/suggestions/generate?date=${date}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminSuggestions", date] }); toast.success("Fresh suggestions generated"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const subPromoMut = useMutation({
    mutationFn: ({ pct, active }) => api.post("/admin/sub-promo", { pct, active }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["subPromo"] });
      toast.success(r.data.active ? `${r.data.pct}% off Pro & VIP is ON` : "Subscription discount turned OFF");
    },
    onError: (e) => toast.error(apiError(e)),
  });
  const confirmReset = () => {
    if (window.confirm("Delete ALL picks from before today? Hit Tracker stats (record, units, ROI) will reset to 0. Today's picks are kept. This cannot be undone.")) {
      resetHistoryMut.mutate();
    }
  };

  const input = "bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-3 py-2 text-sm outline-none transition-colors duration-200 w-full";

  const autoFill = () => {
    if (form.pick_type === "batter") {
      const p = (playersData?.players || []).find((x) => x.mlb_id === form.mlb_id);
      if (!p) return toast.error("Pick a batter from the roster search first");
      setForm({ ...form, bet: "To Hit a HR", odds: p.odds?.best_odds ?? form.odds });
      toast.success(`Auto-filled: To Hit a HR ${fmtOdds(p.odds?.best_odds)} (best line on the sheet)`);
    } else if (form.pick_type === "pitcher") {
      const x = (allPitchers?.pitchers || []).find((z) => z.id === form.mlb_id);
      if (!x) return toast.error("Pick a pitcher from the roster search first");
      const line = Math.max(Math.round((x.k9 || 8) * 0.62) - 0.5, 4.5);
      setForm({ ...form, bet: `Over ${line} Strikeouts`, odds: -118 });
      toast.success(`Auto-filled: Over ${line} Strikeouts -118 (from ${x.k9} K/9)`);
    } else {
      const g = (linesData?.games || []).find((x) => `${x.away} @ ${x.home}` === form.player_name);
      if (!g) return toast.error("Pick today's game from the dropdown first");
      const homeFav = g.win_pct_home >= g.win_pct_away;
      setForm({ ...form, bet: `${homeFav ? g.home : g.away} Moneyline`, odds: homeFav ? g.ml_home : g.ml_away });
      toast.success(`Auto-filled: ${homeFav ? g.home : g.away} ML ${fmtOdds(homeFav ? g.ml_home : g.ml_away)} (${homeFav ? g.win_pct_home : g.win_pct_away}% win prob)`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 fade-up">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Admin <span className="gold-text">Desk</span></h1>
          <p className="text-neutral-500 text-sm mt-1">Post picks, grade results, manage members.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowEngine((v) => !v)} data-testid="admin-toggle-engine-btn"
            title="Hidden by default so engine picks never show on screen recordings"
            className={`flex items-center gap-2 text-sm px-4 py-2 rounded border transition-colors duration-200 ${showEngine ? "border-[#a855f7]/60 text-[#a855f7] bg-[#a855f7]/10" : "border-white/20 text-neutral-400 hover:border-[#a855f7]/50"}`}>
            {showEngine ? <EyeOff size={14} /> : <Eye size={14} />} Engine Picks: {showEngine ? "Shown" : "Hidden"}
          </button>
          <button onClick={() => promoMut.mutate(!promo?.active)} disabled={promoMut.isPending} data-testid="admin-promo-toggle-btn"
            className={`flex items-center gap-2 text-sm px-4 py-2 rounded border transition-colors duration-200 ${promo?.active ? "border-[#22c55e]/60 text-[#22c55e] bg-[#22c55e]/10" : "border-white/20 text-neutral-400 hover:border-[#22c55e]/50"}`}>
            <Ticket size={14} /> $1 Promo: {promo?.active ? "ON" : "OFF"}
          </button>
          <button onClick={() => subPromoMut.mutate({ pct: 50, active: !(subPromo?.active && subPromo?.pct === 50) })} disabled={subPromoMut.isPending} data-testid="admin-subpromo-50-btn"
            className={`flex items-center gap-2 text-sm px-4 py-2 rounded border transition-colors duration-200 ${subPromo?.active && subPromo?.pct === 50 ? "border-[#22c55e]/60 text-[#22c55e] bg-[#22c55e]/10 glow-pulse" : "border-white/20 text-neutral-400 hover:border-[#22c55e]/50"}`}>
            <Percent size={14} /> 50% Off Subs: {subPromo?.active && subPromo?.pct === 50 ? "ON" : "OFF"}
          </button>
          <button onClick={() => subPromoMut.mutate({ pct: 25, active: !(subPromo?.active && subPromo?.pct === 25) })} disabled={subPromoMut.isPending} data-testid="admin-subpromo-25-btn"
            className={`flex items-center gap-2 text-sm px-4 py-2 rounded border transition-colors duration-200 ${subPromo?.active && subPromo?.pct === 25 ? "border-[#22c55e]/60 text-[#22c55e] bg-[#22c55e]/10 glow-pulse" : "border-white/20 text-neutral-400 hover:border-[#22c55e]/50"}`}>
            <Percent size={14} /> 25% Off Subs: {subPromo?.active && subPromo?.pct === 25 ? "ON" : "OFF"}
          </button>
          <button onClick={() => discordMut.mutate()} disabled={discordMut.isPending} data-testid="admin-discord-post-btn"
            className="flex items-center gap-2 border border-[#5865F2]/50 text-[#8b96f5] hover:border-[#5865F2] text-sm px-4 py-2 rounded transition-colors duration-200">
            <Send size={14} /> {discordMut.isPending ? "Posting..." : "Post to Discord"}
          </button>
          <button onClick={confirmReset} disabled={resetHistoryMut.isPending} data-testid="admin-reset-history-btn"
            className="flex items-center gap-2 border border-red-500/30 text-red-400 hover:border-red-400 text-sm px-4 py-2 rounded transition-colors duration-200">
            <Eraser size={14} /> Reset History
          </button>
          <button onClick={() => refreshMut.mutate()} disabled={refreshMut.isPending} data-testid="admin-refresh-stats-btn"
            className="flex items-center gap-2 border border-white/20 hover:border-[#a855f7] text-sm px-4 py-2 rounded transition-colors duration-200">
            <RefreshCw size={14} className={refreshMut.isPending ? "animate-spin" : ""} /> Refresh MLB Stats
          </button>
        </div>
      </div>

      <FreeModeSection />

      {picksData && (picksData.picks || []).length === 0 && date === today && (
        <div className="card-gold border border-[#f59e0b]/50 rounded-xl p-4 flex flex-wrap items-center gap-3 fade-up-1" data-testid="admin-empty-board-banner">
          <div className="flex-1 min-w-[240px]">
            <div className="font-display text-lg font-bold uppercase text-[#f59e0b]">Today's board is empty</div>
            <p className="text-xs text-neutral-400 mt-0.5">Nothing gets auto-posted anymore. Want the engine to draft today's board? You review every pick, fix any odds, and only what you approve hits the board.</p>
          </div>
          <button onClick={() => { setShowEngine(true); suggRegenMut.mutate(); }} disabled={suggRegenMut.isPending} data-testid="admin-generate-board-btn"
            className="flex items-center gap-2 bg-[#f59e0b] hover:bg-[#d97706] text-black font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded transition-colors duration-200">
            <Sparkles size={14} /> {suggRegenMut.isPending ? "Drafting picks..." : "Generate Today's Board"}
          </button>
        </div>
      )}

      {showEngine && (
      <div className="card-gold border border-[#a855f7]/30 rounded-xl p-5 fade-up-1" data-testid="admin-suggestions-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div>
            <h3 className="font-display text-lg font-bold uppercase text-[#a855f7] flex items-center gap-2"><Sparkles size={16} /> Auto-Suggested Picks — {suggData?.date || date}
              {suggData?.record && (suggData.record.wins + suggData.record.losses) > 0 && (
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#22c55e] border border-[#22c55e]/40 bg-[#22c55e]/10 px-2 py-0.5 rounded-full" data-testid="engine-record-badge">
                  Engine: {suggData.record.wins}W-{suggData.record.losses}L
                </span>
              )}
            </h3>
            <p className="text-xs text-neutral-500 mt-0.5">Generated fresh every morning at 9 AM ET — built to cash: hot high-average bats for Locks, model-edge HR plays for Value, live-swing bombs for Long Shots. Approve to post to the board.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => suggRegenMut.mutate()} disabled={suggRegenMut.isPending} data-testid="admin-sugg-regen-btn"
              className="flex items-center gap-1.5 border border-white/20 hover:border-[#a855f7] text-xs uppercase tracking-wider font-bold px-3 py-2 rounded transition-colors duration-200">
              <RefreshCw size={12} className={suggRegenMut.isPending ? "animate-spin" : ""} /> Regenerate
            </button>
            {(suggData?.suggestions || []).some((s) => s.status === "suggested") && (
              <button onClick={() => suggApproveAllMut.mutate()} disabled={suggApproveAllMut.isPending} data-testid="admin-sugg-approve-all-btn"
                className="flex items-center gap-1.5 bg-[#22c55e] hover:bg-[#16a34a] text-black text-xs uppercase tracking-wider font-bold px-3 py-2 rounded transition-colors duration-200">
                <Check size={13} /> Approve All
              </button>
            )}
          </div>
        </div>
        <div className="mt-3 grid md:grid-cols-2 gap-2.5">
          {(suggData?.suggestions || []).map((s) => (
            <div key={s.id} data-testid={`admin-sugg-${s.id}`}
              className={`bg-black/40 border rounded-lg p-3 ${s.status === "approved" ? "border-[#22c55e]/40 opacity-60" : s.status === "dismissed" ? "border-white/5 opacity-40" : "border-white/10"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${s.category === "lock" ? "bg-[#a855f7]/20 text-[#a855f7]" : s.category === "value" ? "bg-[#22c55e]/20 text-[#22c55e]" : "bg-purple-500/20 text-purple-300"}`}>{s.category}</span>
                  <div className="font-semibold text-sm mt-1 truncate">{s.player_name} <span className="text-neutral-600 text-xs">{s.team}</span></div>
                  <div className="text-xs text-neutral-400">{s.bet} · {s.status === "suggested" ? <SuggOdds s={s} qc={qc} date={date} /> : <span className="font-data text-[#22c55e]">{fmtOdds(s.odds)}</span>} · conf {s.confidence}</div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {s.status === "suggested" ? (
                    <>
                      <button onClick={() => suggApproveMut.mutate(s.id)} disabled={suggApproveMut.isPending} data-testid={`admin-sugg-approve-${s.id}`} title="Approve — post to board"
                        className="p-1.5 rounded border border-[#22c55e]/50 text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors duration-200"><Check size={14} /></button>
                      <button onClick={() => suggDismissMut.mutate(s.id)} disabled={suggDismissMut.isPending} data-testid={`admin-sugg-dismiss-${s.id}`} title="Dismiss"
                        className="p-1.5 rounded border border-white/15 text-neutral-500 hover:text-red-400 hover:border-red-400/50 transition-colors duration-200"><X size={14} /></button>
                    </>
                  ) : (
                    <span className={`text-[10px] uppercase tracking-widest font-bold ${s.status === "approved" ? "text-[#22c55e]" : "text-neutral-600"}`}>{s.status}</span>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-neutral-500 italic mt-1.5">"{s.note}"</p>
            </div>
          ))}
          {(suggData?.suggestions || []).length === 0 && <p className="text-neutral-500 text-sm p-2">No suggestions for this date yet — hit Regenerate.</p>}
        </div>
      </div>
      )}

      <div className="card-surface rounded-xl p-5 fade-up-1">
        <h3 className="font-display text-lg font-bold uppercase text-[#a855f7] mb-3">Post a Pick</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-[11px] uppercase text-neutral-500 font-semibold">Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="admin-date-input" className={input} />
          </div>
          <div>
            <label className="text-[11px] uppercase text-neutral-500 font-semibold">Sport</label>
            <select value={form.sport} data-testid="admin-sport-select"
              onChange={(e) => {
                const v = e.target.value;
                setForm({ ...form, sport: v, pick_type: v === "mlb" ? "batter" : "team", category: form.category === "fish" ? "lock" : form.category, mlb_id: null, player_name: "", team: "", bet: "" });
                setRosterQ("");
              }}
              className={input}>
              {SPORT_OPTIONS.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
            </select>
          </div>
          {form.sport === "mlb" && (
          <div>
            <label className="text-[11px] uppercase text-neutral-500 font-semibold">Pick Type</label>
            <select value={form.pick_type} data-testid="admin-picktype-select"
              onChange={(e) => {
                const t = e.target.value;
                setForm({ ...form, pick_type: t, category: t === "fish" ? "fish" : form.category === "fish" ? "lock" : form.category, player_name: "", mlb_id: null, team: "", bet: "" });
                setRosterQ("");
              }} className={input}>
              <option value="batter">Batter Prop</option>
              <option value="pitcher">Pitcher Prop (Ks, walks...)</option>
              <option value="fish">Fish of The Day (ML / Spread / O-U)</option>
            </select>
          </div>
          )}
          {form.pick_type !== "fish" && (
            <div>
              <label className="text-[11px] uppercase text-neutral-500 font-semibold">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-category-select" className={input}>
                <option value="lock">Lock (5★)</option>
                <option value="value">Value (4★)</option>
                <option value="longshot">Long Shot (3★)</option>
              </select>
            </div>
          )}
          {form.pick_type !== "fish" && form.sport === "mlb" && (
            <div className="relative">
              <label className="text-[11px] uppercase text-neutral-500 font-semibold">From roster — type a name</label>
              <input value={rosterQ} onChange={(e) => setRosterQ(e.target.value)} data-testid="admin-roster-search" className={input} placeholder={form.pick_type === "pitcher" ? "e.g. Skenes" : "e.g. Schwarber"} />
              {rosterQ.trim().length >= 2 && (
                <div className="absolute z-30 mt-1 w-full bg-black border border-[#a855f7]/40 rounded-lg max-h-60 overflow-y-auto shadow-xl" data-testid="admin-roster-results">
                  {rosterMatches.map((p) => (
                    <button key={p.mlb_id} data-testid={`admin-roster-opt-${p.mlb_id}`}
                      onClick={() => { setForm({ ...form, player_name: p.name, team: p.team, mlb_id: p.mlb_id }); setRosterQ(""); }}
                      className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-neutral-300 hover:bg-[#a855f7]/10 transition-colors duration-200">
                      <img src={p.headshot} alt="" className="w-7 h-7 rounded-full object-cover bg-white/5" loading="lazy" />
                      <span className="truncate">{p.name}</span>
                      <span className="text-neutral-600 text-xs shrink-0">{p.team} · {p.position}</span>
                    </button>
                  ))}
                  {rosterMatches.length === 0 && (
                    <div className="px-3 py-2.5 text-xs text-neutral-500" data-testid="admin-roster-no-match">No {form.pick_type} matches "{rosterQ}" — check the spelling</div>
                  )}
                </div>
              )}
            </div>
          )}
          <div>
            <label className="text-[11px] uppercase text-neutral-500 font-semibold">{form.sport !== "mlb" ? "Matchup / Player" : form.pick_type === "fish" ? "Matchup / Team" : "Player name"}</label>
            {form.sport !== "mlb" && (
              <select data-testid="admin-sport-game-select" className={`${input} mb-1.5`}
                value={sportGames.some((g) => g.label === form.player_name) ? form.player_name : ""}
                onChange={(e) => { if (e.target.value) setForm({ ...form, player_name: e.target.value }); }}>
                <option value="">{sportGames.length ? `— Pick from the ${form.sport.toUpperCase()} slate —` : `No ${form.sport.toUpperCase()} slate data yet — type it below`}</option>
                {sportGames.map((g, i) => <option key={i} value={g.label}>{g.label}{g.time ? ` · ${g.time}` : ""}</option>)}
              </select>
            )}
            {form.pick_type === "fish" && (
              <select data-testid="admin-game-select" className={`${input} mb-1.5`}
                value={(linesData?.games || []).some((g) => `${g.away} @ ${g.home}` === form.player_name) ? form.player_name : ""}
                onChange={(e) => { if (e.target.value) setForm({ ...form, player_name: e.target.value, team: "" }); }}>
                <option value="">— Pick from today's games —</option>
                {(linesData?.games || []).map((g, i) => (
                  <option key={i} value={`${g.away} @ ${g.home}`}>{g.away} @ {g.home}{g.time_et ? ` · ${g.time_et}` : ""}</option>
                ))}
              </select>
            )}
            <input value={form.player_name} onChange={(e) => setForm({ ...form, player_name: e.target.value })} data-testid="admin-player-input" className={input} placeholder={(TYPE_HINTS[form.pick_type] || { player: "e.g. Bengals ML or Joe Burrow", bet: "e.g. ML, -3.5, Over 44.5" }).player} />
          </div>
          <div>
            <label className="text-[11px] uppercase text-neutral-500 font-semibold flex items-center justify-between">Bet
              {form.sport === "mlb" && (
              <button onClick={autoFill} data-testid="admin-autofill-btn" title="Auto-generate the bet with the correct odds"
                className="inline-flex items-center gap-1 text-[10px] normal-case font-bold text-[#a855f7] border border-[#a855f7]/40 hover:bg-[#a855f7]/10 px-1.5 py-0.5 rounded transition-colors duration-200">
                <Wand2 size={10} /> Auto-fill
              </button>
              )}
            </label>
            <input value={form.bet} onChange={(e) => setForm({ ...form, bet: e.target.value })} data-testid="admin-bet-input" className={input} placeholder={(TYPE_HINTS[form.pick_type] || { player: "e.g. Bengals ML or Joe Burrow", bet: "e.g. ML, -3.5, Over 44.5" }).bet} />
          </div>
          <div>
            <label className="text-[11px] uppercase text-neutral-500 font-semibold">Odds (American)</label>
            <input type="number" value={form.odds} onChange={(e) => setForm({ ...form, odds: e.target.value })} data-testid="admin-odds-input" className={input} />
          </div>
          <div>
            <label className="text-[11px] uppercase text-neutral-500 font-semibold">Confidence (1-10)</label>
            <input type="number" step="0.1" min="1" max="10" value={form.confidence} onChange={(e) => setForm({ ...form, confidence: e.target.value })} data-testid="admin-confidence-input" className={input} />
          </div>
          <div className="sm:col-span-2 lg:col-span-1">
            <label className="text-[11px] uppercase text-neutral-500 font-semibold">Note</label>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="admin-note-input" className={input} placeholder="Why this hits..." />
          </div>
        </div>
        <button onClick={() => createMut.mutate()} disabled={!form.player_name || createMut.isPending} data-testid="admin-post-pick-btn"
          className="mt-4 bg-[#a855f7] hover:bg-[#9333ea] disabled:opacity-40 text-black font-bold uppercase tracking-wider text-sm px-6 py-2.5 rounded transition-colors duration-200">
          Post Pick
        </button>
      </div>

      <div className="card-surface rounded-xl overflow-x-auto fade-up-2">
        <div className="p-4 border-b border-white/10 font-display text-lg font-bold uppercase">Picks — {date}</div>
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="text-neutral-500 text-[11px] uppercase tracking-wider text-left border-b border-white/10">
              <th className="p-3">Category</th><th className="p-3">Player</th><th className="p-3">Bet</th><th className="p-3">Odds</th><th className="p-3">Result</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {(picksData?.picks || []).map((p) => (
              <tr key={p.id} className="border-b border-white/5" data-testid={`admin-pick-row-${p.id}`}>
                <td className="p-3 uppercase text-[#a855f7] text-xs font-bold">{p.category}</td>
                <td className="p-3 font-medium">{p.player_name} <span className="text-neutral-600 text-xs">{p.team}</span></td>
                <td className="p-3 text-neutral-400">{p.bet}</td>
                <td className="p-3 font-data">{fmtOdds(p.odds)}</td>
                <td className="p-3">
                  <select value={p.result} onChange={(e) => gradeMut.mutate({ id: p.id, result: e.target.value })} data-testid={`admin-grade-select-${p.id}`}
                    className={`bg-[#0a0a0a] border border-white/15 rounded px-2 py-1 text-xs ${p.result === "win" ? "text-[#22c55e]" : p.result === "loss" ? "text-red-400" : "text-neutral-300"}`}>
                    <option value="pending">pending</option>
                    <option value="win">win</option>
                    <option value="loss">loss</option>
                    <option value="push">push</option>
                  </select>
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => delMut.mutate(p.id)} data-testid={`admin-delete-pick-${p.id}`} className="text-neutral-600 hover:text-red-400 transition-colors duration-200"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card-surface rounded-xl p-4 fade-up-3" data-testid="admin-revenue-section">
        <div className="font-display text-lg font-bold uppercase mb-3">Revenue</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[["promo_pass", "$1 Launch Passes"], ["daypass", "Day Passes"], ["pro", "Pro Subs"], ["vip", "VIP Subs"]].map(([k, label]) => (
            <div key={k} className="bg-[#0a0a0a] border border-white/10 rounded-lg p-3" data-testid={`revenue-${k}`}>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">{label}</div>
              <div className="font-data text-2xl font-bold mt-1">{revenue?.summary?.[k]?.count || 0}</div>
              <div className="text-xs text-[#22c55e] font-data">${(revenue?.summary?.[k]?.revenue || 0).toFixed(2)}</div>
            </div>
          ))}
          <div className="bg-[#a855f7]/10 border border-[#a855f7]/40 rounded-lg p-3" data-testid="revenue-total">
            <div className="text-[10px] uppercase tracking-widest text-[#a855f7] font-bold">Total Revenue</div>
            <div className="font-data text-2xl font-bold mt-1 text-[#a855f7]">${(revenue?.total_revenue || 0).toFixed(2)}</div>
            <div className="text-xs text-neutral-500 font-data">{revenue?.total_sales || 0} sales</div>
          </div>
        </div>
        {(revenue?.recent || []).length > 0 && (
          <table className="w-full text-sm mt-4" data-testid="revenue-recent-table">
            <thead>
              <tr className="text-neutral-500 text-[11px] uppercase tracking-wider text-left border-b border-white/10">
                <th className="p-2">Member</th><th className="p-2">Package</th><th className="p-2 text-right">Amount</th><th className="p-2 text-right">Date</th>
              </tr>
            </thead>
            <tbody>
              {revenue.recent.map((t, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-[#1a1a1a] transition-colors duration-200">
                  <td className="p-2 text-neutral-300 truncate max-w-[200px]">{t.email}</td>
                  <td className="p-2"><span className={`text-[10px] uppercase tracking-widest font-bold ${t.package_id === "vip" ? "text-[#a855f7]" : t.package_id === "pro" ? "text-[#22c55e]" : "text-neutral-400"}`}>{t.package_id}{t.package_id === "daypass" && Number(t.amount) <= 1.01 ? " · $1 promo" : ""}</span></td>
                  <td className="p-2 text-right font-data text-[#22c55e]">${Number(t.amount).toFixed(2)}</td>
                  <td className="p-2 text-right text-neutral-500 font-data text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AffiliateLinksSection />


      <div className="card-surface rounded-xl overflow-x-auto fade-up-3">
        <div className="p-4 border-b border-white/10 font-display text-lg font-bold uppercase">Members ({usersData?.users?.length || 0})</div>
        <table className="w-full text-sm min-w-[600px]">
          <thead>
            <tr className="text-neutral-500 text-[11px] uppercase tracking-wider text-left border-b border-white/10">
              <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Tier</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {(usersData?.users || []).map((u) => (
              <tr key={u.id} className="border-b border-white/5" data-testid={`admin-user-row-${u.id}`}>
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3 text-neutral-400">{u.email}</td>
                <td className="p-3 uppercase text-xs">{u.role}</td>
                <td className="p-3">
                  <select value={u.tier} onChange={(e) => tierMut.mutate({ id: u.id, tier: e.target.value })} data-testid={`admin-tier-select-${u.id}`}
                    className="bg-[#0a0a0a] border border-white/15 rounded px-2 py-1 text-xs">
                    <option value="free">free</option>
                    <option value="pro">pro</option>
                    <option value="vip">vip</option>
                  </select>
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => resetLinkMut.mutate(u.id)} data-testid={`admin-reset-link-${u.id}`}
                    className="text-[11px] uppercase tracking-wider font-bold text-neutral-500 hover:text-[#a855f7] border border-white/15 hover:border-[#a855f7]/50 rounded px-2.5 py-1 transition-colors duration-200">
                    Reset link
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
